import { action, makeObservable, observable, ObservableMap } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import { normalizeFilename } from "@/modules/file/normalizeFileName";
import { checkIfFileCanBeUploaded } from "@/modules/file/validation";
import { makeUploadUrlParts, uploadFile } from "@/modules/file/upload";
import { uploadImportFile } from "@/modules/file-import/upload";
import { MemCommonEditorFileInfo } from "@mem-labs/common-editor";
import { FileDropItemWithPath } from "@/modules/file/dropItems";
import pThrottle from "p-throttle";
import axios, { CancelTokenSource } from "axios";

export interface FileUploadProgress {
  filename: string;
  uploadedBytes: number;
  totalBytes: number;
  status: "pending" | "uploading" | "completed" | "error";
  error?: Error;
}

export interface FileUploadResult {
  info: MemCommonEditorFileInfo;
  fileItem: FileDropItemWithPath;
  uploadedFileBlobName: string;
  signedUrl: string;
}

export enum ImportFileUploaderKind {
  LegacySignedUrl = "legacy-signed-url",
  StorageApi = "storage-api",
}

export interface UploadFileItemsOptions {
  uploader?: ImportFileUploaderKind;
}
export class AppStoreUploadProgressStore extends AppSubStore {
  // Track individual file upload progress
  private fileProgressMap = new ObservableMap<Uuid, FileUploadProgress>();
  private fileResultsMap = new ObservableMap<Uuid, FileUploadResult>();
  // Track cancel sources per session so we can abort all uploads for a session
  private sessionCancelSources = new ObservableMap<Uuid, Set<CancelTokenSource>>();
  private sessionAbortControllers = new ObservableMap<Uuid, Set<AbortController>>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      AppStoreUploadProgressStore,
      | "fileProgressMap"
      | "fileResultsMap"
      | "sessionCancelSources"
      | "sessionAbortControllers"
      | "uploadFileItemViaSignedUrl"
      | "uploadFileItemViaTus"
    >(this, {
      cancelUploadsBySession: true,
      getFileProgresses: true,
      getFileResults: true,
      getFileProgress: true,
      getFileResult: true,

      fileProgressMap: observable,
      fileResultsMap: observable,
      sessionCancelSources: observable,
      sessionAbortControllers: observable,

      updateFileProgress: action,
      clearFileProgress: action,
      uploadFileItem: action,
      uploadFileItems: action,
      resetState: action,
      uploadFileItemViaSignedUrl: false,
      uploadFileItemViaTus: false,
    });
  }

  getFileProgress(fileId: Uuid): FileUploadProgress | undefined {
    return this.fileProgressMap.get(fileId);
  }

  getFileResult(fileId: Uuid): FileUploadResult | undefined {
    return this.fileResultsMap.get(fileId);
  }

  getFileProgresses(fileIds: Uuid[]): FileUploadProgress[] {
    return fileIds
      .map(id => {
        return this.getFileProgress(id);
      })
      .filter(Boolean) as FileUploadProgress[];
  }

  getFileResults(fileIds: Uuid[]): FileUploadResult[] {
    return fileIds.map(id => this.getFileResult(id)).filter(Boolean) as FileUploadResult[];
  }

  // File progress methods
  updateFileProgress(fileId: Uuid, progress: Partial<FileUploadProgress>) {
    const currentProgress = this.fileProgressMap.get(fileId) || {
      filename: "",
      uploadedBytes: 0,
      totalBytes: 0,
      status: "pending" as const,
    };

    this.fileProgressMap.set(fileId, {
      ...currentProgress,
      ...progress,
    });
  }

  clearFileProgress(fileId: Uuid) {
    this.fileProgressMap.delete(fileId);
  }

  async uploadFileItem(
    fileItem: FileDropItemWithPath,
    sessionId: Uuid,
    options?: UploadFileItemsOptions
  ): Promise<Uuid> {
    const uploader = options?.uploader ?? ImportFileUploaderKind.LegacySignedUrl;
    if (uploader === ImportFileUploaderKind.StorageApi) {
      return this.uploadFileItemViaTus(fileItem, sessionId);
    }

    return this.uploadFileItemViaSignedUrl(fileItem, sessionId);
  }

  private async uploadFileItemViaSignedUrl(
    fileItem: FileDropItemWithPath,
    sessionId: Uuid
  ): Promise<Uuid> {
    const file = await fileItem.getFile();
    const fileId = uuidModule.generate();

    this.updateFileProgress(fileId, {
      filename: fileItem.name,
      status: "pending",
      uploadedBytes: 0,
      totalBytes: file.size,
    });

    const normalizedFileName = normalizeFilename({ fileName: fileItem.name });
    const info = {
      fileId,
      normalizedFileName,
      fileMimeType: fileItem.type,
      encodedFileContent: file.name, // TODO: write a comment explaining this unexpected key/val
      fileSizeBytes: file.size,
    };

    const canBeUploaded = checkIfFileCanBeUploaded({ info });
    if (!canBeUploaded) {
      throw new Error("File can not be uploaded");
    }

    // Create and record a cancel token for this upload under the session
    const source = axios.CancelToken.source();
    let uploadResult;
    try {
      const cancelSet = this.sessionCancelSources.get(sessionId) || new Set<CancelTokenSource>();
      cancelSet.add(source);
      this.sessionCancelSources.set(sessionId, cancelSet);

      uploadResult = await uploadFile({
        info,
        blob: file,
        config: {
          cancelToken: source.token,
          onUploadProgress: progress => {
            const status: FileUploadProgress["status"] =
              progress.loaded === progress.total ? "completed" : "pending";
            this.updateFileProgress(fileId, {
              uploadedBytes: progress.loaded,
              totalBytes: progress.total,
              status,
            });
          },
        },
      });
    } finally {
      // Remove the source from the session set regardless of outcome
      const cancelSet = this.sessionCancelSources.get(sessionId);
      if (cancelSet) {
        cancelSet.delete(source);
        if (cancelSet.size === 0) {
          this.sessionCancelSources.delete(sessionId);
        } else {
          this.sessionCancelSources.set(sessionId, cancelSet);
        }
      }
    }

    const { signedUrl } = uploadResult;
    const urlParts = makeUploadUrlParts(signedUrl);
    if (!urlParts.blobName) {
      throw new Error("Invalid signed url");
    }

    this.fileResultsMap.set(fileId, {
      info,
      fileItem,
      uploadedFileBlobName: urlParts.blobName,
      signedUrl,
    });

    return fileId;
  }

  private async uploadFileItemViaTus(
    fileItem: FileDropItemWithPath,
    sessionId: Uuid
  ): Promise<Uuid> {
    const file = await fileItem.getFile();
    const fileId = uuidModule.generate();

    this.updateFileProgress(fileId, {
      filename: fileItem.name,
      status: "pending",
      uploadedBytes: 0,
      totalBytes: file.size,
    });

    const normalizedFileName = normalizeFilename({ fileName: fileItem.name });
    const info = {
      fileId,
      normalizedFileName,
      fileMimeType: fileItem.type,
      encodedFileContent: file.name,
      fileSizeBytes: file.size,
    };

    const canBeUploaded = checkIfFileCanBeUploaded({ info });
    if (!canBeUploaded) {
      throw new Error("File can not be uploaded");
    }

    const controller = new AbortController();
    try {
      const abortSet = this.sessionAbortControllers.get(sessionId) || new Set<AbortController>();
      abortSet.add(controller);
      this.sessionAbortControllers.set(sessionId, abortSet);

      const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
      const uploadResult = await uploadImportFile({
        blob: file,
        fileId,
        filename: fileItem.name,
        mimeType: fileItem.type,
        config: { spaceAccountId },
        onProgress: percent => {
          const uploadedBytes = Math.round((percent / 100) * file.size);
          const status: FileUploadProgress["status"] = percent >= 100 ? "completed" : "pending";
          this.updateFileProgress(fileId, {
            uploadedBytes,
            totalBytes: file.size,
            status,
          });
        },
        signal: controller.signal,
      });

      if (!uploadResult.success) {
        throw new Error(uploadResult.error);
      }

      this.fileResultsMap.set(fileId, {
        info,
        fileItem,
        uploadedFileBlobName: uploadResult.blobName,
        signedUrl: "",
      });

      this.updateFileProgress(fileId, {
        uploadedBytes: file.size,
        totalBytes: file.size,
        status: "completed",
      });
    } finally {
      const abortSet = this.sessionAbortControllers.get(sessionId);
      if (abortSet) {
        abortSet.delete(controller);
        if (abortSet.size === 0) {
          this.sessionAbortControllers.delete(sessionId);
        } else {
          this.sessionAbortControllers.set(sessionId, abortSet);
        }
      }
    }

    return fileId;
  }

  async uploadFileItems(
    fileItems: FileDropItemWithPath[],
    sessionId: Uuid,
    options?: UploadFileItemsOptions
  ): Promise<Uuid[]> {
    const uploader = options?.uploader ?? ImportFileUploaderKind.LegacySignedUrl;
    const throttle =
      uploader === ImportFileUploaderKind.StorageApi
        ? pThrottle({ limit: 20, interval: 1000 })
        : pThrottle({ limit: 10, interval: 3000 });
    const throttledUpload = throttle((fileItem: FileDropItemWithPath) =>
      this.uploadFileItem(fileItem, sessionId, { uploader })
    );

    // Start all uploads - p-throttle will automatically queue and rate-limit them
    const uploadPromises = fileItems.map(fileItem => throttledUpload(fileItem));
    return Promise.all(uploadPromises);
  }

  /** Abort all in-flight uploads for a given session */
  cancelUploadsBySession(sessionId: Uuid) {
    const cancelSet = this.sessionCancelSources.get(sessionId);
    if (cancelSet && cancelSet.size > 0) {
      for (const source of cancelSet) {
        try {
          source.cancel("canceled-by-user");
        } catch {
          // noop
        }
      }
      this.sessionCancelSources.delete(sessionId);
    }

    const abortSet = this.sessionAbortControllers.get(sessionId);
    if (abortSet && abortSet.size > 0) {
      for (const controller of abortSet) {
        try {
          controller.abort();
        } catch {
          // noop
        }
      }
      this.sessionAbortControllers.delete(sessionId);
    }
  }

  // Reset all state
  resetState() {
    this.fileProgressMap.clear();
    this.fileResultsMap.clear();
    this.sessionCancelSources.clear();
    this.sessionAbortControllers.clear();
  }
}
